import React from 'react';
import { useIntl } from 'react-intl';
import cn from 'classnames';
import { observer } from 'mobx-react';

import { CollectionProgramAsset } from '@kaltura-ott/tvpil-shared';

import AirTimeV2 from 'components/presenters/AirTimeV2/AirTimeV2';
import { InfoButton } from 'components/presenters/InfoButton/InfoButton';
import { AiringDateProps } from 'components/widgets/CardView/types';
import { useContentRestriction } from 'hooks';

import { CardChannelName } from '../CardChannelName/CardChannelName';
import CardTitle from '../CardTitle/CardTitle';

import { useHandleRef } from './hooks/useRefHook/useRefHook';

import styles from './CardBubbleInfo.module.scss';

function CardBubbleInfo({
  seasonEpisodeTitle,
  title,
  isMosaicProgram,
  isExternalProviderAsset = false,
  handleClickInfo,
  channelHasProgram,
  channelName,
  isAdult,
  collectionProgramAsset,
  isLinkAsAsset,
  shouldShowAiringTime,
  airingDate,
  isRecord,
  isSeriesRecord,
}: {
  isAdult?: boolean;
  isExternalProviderAsset?: boolean;
  seasonEpisodeTitle?: string;
  title: string;
  isMosaicProgram: boolean;
  handleClickInfo: (e: React.MouseEvent) => void;
  channelHasProgram: boolean;
  channelName?: string;
  /**
   * @deprecated Use shouldShowAiringTime, label and startDate props instead.
   */
  collectionProgramAsset?: CollectionProgramAsset;
  isLinkAsAsset: boolean;
  shouldShowAiringTime?: boolean;
  airingDate?: AiringDateProps | undefined;
  isRecord: boolean;
  isSeriesRecord: boolean;
}) {
  const ref = useHandleRef();
  const intl = useIntl();
  // for avoiding wrapping the whole parent component with observer
  const isContentRestricted = useContentRestriction(Boolean(isAdult)) && !isExternalProviderAsset;

  return (
    <button
      ref={ref}
      type='button'
      data-type='cardBubbleInfo'
      onClick={isContentRestricted ? undefined : handleClickInfo}
      tabIndex={0}
      aria-label={intl.formatMessage({ id: 'General.Button.Info', defaultMessage: 'click for more info' })}
      className={cn(styles.wrapper, { [styles.noOpacity]: isLinkAsAsset })}
    >
      <div aria-hidden>
        <CardTitle
          isRecord={isRecord}
          isSeriesRecord={isSeriesRecord}
          isAdult={isAdult}
          seasonEpisodeTitle={seasonEpisodeTitle}
          title={title}
          isMosaicProgram={isMosaicProgram}
        />
        {!isContentRestricted && (
          <>
            {!!seasonEpisodeTitle && <p className={styles.description}>{seasonEpisodeTitle}</p>}
            {channelName && (
              <div className={styles.channel}>
                <CardChannelName channelName={channelName} />
              </div>
            )}
            {(collectionProgramAsset || shouldShowAiringTime) && (
              <AirTimeV2
                className={styles.airTime}
                airingDate={airingDate}
                isInfo
              />
            )}
            {channelHasProgram && <InfoButton className={styles.infoButton} />}
          </>
        )}
      </div>
    </button>
  );
}

export default observer(CardBubbleInfo);
